import React, { useContext, useEffect } from 'react';
import {
  bool, func, number, string
} from 'prop-types';
import { ExperienceContext, useDomPath, useStoreId } from '@thd-nucleus/experience-context';
import {
  useLazyDataModel,
  arrayOf as arrayOfType,
  string as stringType,
  shape as shapeType,
  bool as boolType,
  number as numberType,
  params,
  client,
  extend
} from '@thd-nucleus/data-sources';
import { SizeGuide } from '@thd-olt-component-react/size-guide';
import {
  attributeValueFilter,
  getAttributeType,
  getSelectedAttribute,
  useSuperSku
} from './hooks/useSuperSku';
import {
  ACTION_TYPES,
  NUMBER_OF_MINI_SWATCHES_ON_DESKTOP,
  NUMBER_OF_MINI_SWATCHES_ON_MOBILE,
  MAXIMUM_CALLS_FOR_PRICE_ENDPOINT,
  MAXIMUM_COMBINATIONS_FOR_PRICE_ENDPOINT
} from './statics';
import './super-sku.scss';
import {
  getSwatchCardCombinationDetails,
  shouldDisplayInventory,
  shouldDisplaySwatchCards,
  shouldDisplayTooltips,
  syncAttributes
} from './super-sku-util';
import { isSdskuDisplayable } from './components/super-duper-sku/super-duper-sku-utils';
import { SuperSkuAttribute } from './components/super-sku-attribute/super-sku-attribute';
import { SuperSkuAttributeValue } from './components/super-sku-attribute-value/super-sku-attribute-value';
import { Alerts } from './components/alerts/alerts';
import { Loader } from './components/loader/loader';
import { LoadingError } from './components/loading-error/loading-error';
import { MiniSwatchTooltip } from './components/mini-swatch-tooltip/mini-swatch-tooltip';
import { MoreOptions } from './components/more-options/more-options';
import { publish } from './publisher';
import { SuperDuperSku } from './components/super-duper-sku/super-duper-sku';
import { SuperSkuCarousel } from './components/super-sku-carousel/SuperSkuCarousel';

export { SuperSkuMiniSwatches } from './components/super-sku-mini-swatches/super-sku-mini-swatches';

export const SuperSku = (props) => {
  const {
    channel = 'desktop',
    segment
  } = useContext(ExperienceContext);

  const defaultStoreId = useStoreId();
  const {
    centeredCards,
    disableMediaAndAvailability,
    hideCards,
    itemId,
    large,
    miniSwatch,
    numberOfMiniSwatches,
    onChange,
    onHover,
    shouldSyncAttributes,
    storeId: storeIdFromProps,
  } = props;

  const storeId = storeIdFromProps || defaultStoreId;

  const [state, dispatch] = useSuperSku({
    itemId,
    storeId,
    disableMediaAndAvailability,
    isMiniSwatch: miniSwatch,
    large
  });

  const [domPath, ref] = useDomPath();

  const handleAttributeValueClick = (event, attributeLabel, attributeValue) => {
    if (shouldSyncAttributes) {
      const { parentId } = state;
      publish('pass-to-collection', { attributeLabel, attributeValue, parentId });
    }
    dispatch({
      type: ACTION_TYPES.SELECT, attributeLabel, attributeValue
    });
    publish('click-swatch', { attributeLabel, attributeValue, path: domPath });
    const tippyPopper = document.querySelector('.tippy-popper');
    if (tippyPopper && tippyPopper._tippy) {
      tippyPopper._tippy.hide();
    }
  };

  const handleAttributeValueHover = (
    event, attributeLabel, attributeValue, isTooltipsEnabled, isMiniSwatch, attributeType
  ) => {
    if (!isTooltipsEnabled && !isMiniSwatch && attributeType === 'dropdown') return;
    dispatch({
      type: ACTION_TYPES.HOVER, attributeLabel, attributeValue
    });
  };

  const handleResetHover = () => {
    dispatch({
      type: ACTION_TYPES.HOVER_RESET
    });
  };

  const handleAttributeOverflowReset = (scrollHeight, attributeName) => {
    dispatch({
      type: ACTION_TYPES.RESET_OVERFLOW_VALUES, scrollHeight, attributeName
    });
  };

  const handleAttributeOverflowUpdate = (attributeRef, attributeName) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_OVERFLOW_VALUES, attributeRef, attributeName
    });
  };

  const handleSdskuAttributeValueClick = (metadata) => {
    dispatch({
      type: ACTION_TYPES.SDSKU_SELECT, metadata
    });
    publish('click-super-duper-sku', { path: domPath });
  };

  const onReloadClick = () => {
    const { location = {} } = window || {};
    location.reload(true);
  };

  let itemIds = state.childItemsLookup?.map((item) => item?.itemId);
  const opts = {
    variables: {
      itemIds,
      storeId
    },
    ssr: false
  };
  const [makeMediaInventoryCall, mediaPriceInventoryData] = useLazyDataModel('mediaPriceInventory', opts);
  const shouldMakeCallMediaCall = disableMediaAndAvailability
    || !itemIds?.length
    || (!shouldDisplayTooltips(state, channel) && !shouldDisplaySwatchCards(state, channel));

  useEffect(() => {
    if (!shouldMakeCallMediaCall) {
      const itemSubset = itemIds.slice(
        0, MAXIMUM_COMBINATIONS_FOR_PRICE_ENDPOINT
      );
      if (itemSubset.length) {
        makeMediaInventoryCall({
          variables: {
            itemIds: itemSubset
          }
        });
      }
    }
  }, [shouldMakeCallMediaCall]);

  const { mediaPriceInventory } = mediaPriceInventoryData?.data || {};

  useEffect(() => {
    if (!mediaPriceInventoryData?.called) return;
    if (mediaPriceInventory
      && (state.childItemsLookup || []).length === mediaPriceInventory?.productDetailsList?.length) {
      dispatch({
        type: ACTION_TYPES.ADD_MEDIA_PRICE_AND_INVENTORY,
        response: mediaPriceInventory,
        channel
      });
    } else if (mediaPriceInventory
      && (state.childItemsLookup || []).length > mediaPriceInventory?.productDetailsList?.length) {
      const itemSubset = itemIds.slice(
        MAXIMUM_COMBINATIONS_FOR_PRICE_ENDPOINT,
        MAXIMUM_COMBINATIONS_FOR_PRICE_ENDPOINT * MAXIMUM_CALLS_FOR_PRICE_ENDPOINT
      );
      if (itemSubset.length) {
        mediaPriceInventoryData.fetchMore({ // need to validate this still works
          variables: {
            itemIds: itemSubset
          },
          updateQuery: (currentItems, { fetchMoreResult: nextBatch }) => {
            if (!nextBatch) return currentItems;
            const { productDetailsList = [], __typename } = nextBatch?.mediaPriceInventory || {};
            const newItems = productDetailsList.filter(({ itemId: newItemId }) => {
              return !(currentItems?.mediaPriceInventory?.productDetailsList.find((item) => item.itemId === newItemId));
            });
            return {
              mediaPriceInventory: {
                productDetailsList: [
                  ...currentItems?.mediaPriceInventory?.productDetailsList,
                  ...newItems
                ],
                __typename
              }
            };
          }
        });
      }
    }
  }, [mediaPriceInventory]);

  useEffect(() => {
    if (state?.itemId && state?.itemId !== itemId) {
      const [, , slug] = state?.canonicalUrl?.split('/');
      onChange({ slug, itemId: state?.itemId, canonicalUrl: state.canonicalUrl });
    }
  }, [state?.itemId]);

  useEffect(() => {
    const { hoveredCombinationDetails } = state || {};
    if (hoveredCombinationDetails && hoveredCombinationDetails?.itemId !== itemId) {
      onHover({
        itemId: hoveredCombinationDetails?.itemId || state?.itemId,
        isHovered: hoveredCombinationDetails?.isHovered
      });
      if (typeof window !== 'undefined') {
        window.LIFE_CYCLE_EVENT_BUS.trigger('supersku.hover', hoveredCombinationDetails?.itemId);
      }
    }
  }, [state?.hoveredCombinationDetails, state?.hoveredCombinationDetails?.itemId]);

  useEffect(() => {
    publish('ready');
  }, []);

  useEffect(() => {
    if (domPath?.length > 0 && state?.parentId) {
      syncAttributes(state, dispatch, domPath);
    }
  }, [domPath?.length, state?.parentId]);

  if (state?.loadingState && !miniSwatch) {
    return <Loader large={large} />;
  }

  const shouldShowAttribute = (attribute) => {
    const { isSwatch, attributeValues } = attribute;
    return attributeValues.length > 1
      && (!miniSwatch || (miniSwatch && isSwatch));
  };

  const hasSwatch = (attributes) => {
    return !!attributes.find(({ isSwatch }) => isSwatch);
  };

  const {
    attributes,
    canonicalUrl,
    childItemsLookup,
    collectionId,
    enabledAndAvailableAttributeValues,
    errorState,
    hoveredCombinationDetails,
    impossibleState,
    isDiscontinued,
    isOutOfStock,
    isTooltipsEnabled,
    itemSizeAndFitDetail,
    info,
    jumpedState,
    noDataState,
    parentId,
    superDuperSku,
    superSkuSizeAndFitDetail,
    unavailableState,
    showAlerts,
    specificationGroup,
    selectedAttributes
  } = state;

  const shouldHideSSKU = attributes?.filter(shouldShowAttribute)?.length === 0;
  if (noDataState || !attributes?.length || shouldHideSSKU) {
    return null;
  }

  const sizeGuideInfo = {
    collectionId,
    itemId,
    itemSizeAndFitDetail,
    parentId,
    superSkuSizeAndFitDetail
  };

  const numberOfMiniSwatchesToDisplay = numberOfMiniSwatches || channel === 'desktop'
    ? NUMBER_OF_MINI_SWATCHES_ON_DESKTOP
    : NUMBER_OF_MINI_SWATCHES_ON_MOBILE;

  const hexColor = segment === 'paint' && specificationGroup?.find(
    (group) => group?.specTitle === 'Details')?.specifications?.find(
    (spec) => /hexadecimal/i.test(spec?.specName))?.specValue;

  const isMobile = channel === 'mobile';
  const paintType = segment === 'paint'
    ? selectedAttributes.find((item) => item?.attributeLabel === 'Paint Type')?.attributeValue : '';

  return (
    <div className="super-sku" ref={ref}>
      {showAlerts && (
        <Alerts
          impossibleState={impossibleState}
          jumpedState={jumpedState}
        />
      )}
      {!errorState
        ? attributes.filter(shouldShowAttribute).map((attribute, attributeIndex) => {
          const { attributeName, attributeValues, isOverflowSection, scrollHeight } = attribute;
          const isHovered = state?.hoveredCombinationDetails?.isHovered;
          const selectedAttribute = getSelectedAttribute(state, attributeName, attributeValues);
          const attributeType = getAttributeType(
            state, channel, attribute, isOverflowSection, hideCards, disableMediaAndAvailability, large
          );
          const enabledTilesInAttribute = enabledAndAvailableAttributeValues?.enabled[attributeName];
          const availableTilesInAttribute = enabledAndAvailableAttributeValues?.available[attributeName];
          const maxItemsToShow = miniSwatch ? numberOfMiniSwatchesToDisplay : attributeValues.length;
          const { attributeValue: selectedAttributeValue, swatchGuid: selectedSwatchGuid } = selectedAttribute || {};

          return (attributeType
            && (
              <SuperSkuAttribute
                attributeName={attributeName}
                attributeValue={isHovered
                  ? hoveredCombinationDetails[attributeName] : selectedAttributeValue}
                swatchGuid={selectedSwatchGuid}
                attributeType={attributeType}
                handleAttributeOverflowReset={handleAttributeOverflowReset}
                handleAttributeOverflowUpdate={handleAttributeOverflowUpdate}
                hexColor={hexColor}
                isOverflowSection={isOverflowSection}
                info={info}
                key={`${attributeName}-${attributeIndex}`}
                large={large}
                miniSwatch={miniSwatch}
                scrollHeight={scrollHeight}
                sizeGuideInfo={sizeGuideInfo}
                paintType={paintType}
              >
                <SuperSkuCarousel
                  shouldUseCarousel={large && !isMobile}
                  carouselProps={{
                    name: 'super-sku-carousel',
                    multiItem: false,
                    itemClass: 'super-sku-item',
                    fade: true,
                    itemWidthPixelsDesktop: 120,
                    fixedItem: true,
                    screenBreakpoint: 550,
                    screenMaxSize: 550,
                  }}
                >
                  {attributeValues
                    .filter((attributeValue) => attributeValueFilter({
                      attributeValue,
                      attribute,
                      enabledTilesInAttribute
                    }))
                    .slice(0, maxItemsToShow)
                    .map((attributeValue) => {
                      const { value, swatchGuid } = attributeValue;
                      const containsInventory = childItemsLookup[0]?.inventory;
                      const isAvailable = (shouldDisplayInventory(state) && containsInventory)
                        ? availableTilesInAttribute?.has(value)
                        : attribute.isAvailable;
                      const swatchCardCombinationDetails = (attributeType === 'card')
                        ? getSwatchCardCombinationDetails(state, attributeName, value) : {};

                      return (
                        <SuperSkuAttributeValue
                          attributeType={attributeType}
                          channel={channel}
                          centeredCards={centeredCards}
                          hexColor={hexColor}
                          hoveredCombinationDetails={hoveredCombinationDetails || {}}
                          isAvailable={isAvailable}
                          isOverflowSection={isOverflowSection}
                          isSelected={selectedAttributeValue === value}
                          isTooltipsEnabled={isTooltipsEnabled}
                          key={`${attributeName}-${value}`}
                          miniSwatch={miniSwatch}
                          onClick={(event) => handleAttributeValueClick(
                            event,
                            attributeName,
                            value
                          )}
                          onMouseEnter={((event) => handleAttributeValueHover(event,
                            attributeName,
                            value,
                            isTooltipsEnabled,
                            miniSwatch,
                            attributeType
                          ))}
                          onMouseLeave={handleResetHover}
                          swatchCardCombinationDetails={swatchCardCombinationDetails}
                          swatchGuid={swatchGuid}
                          value={value}
                        />
                      );
                    })}
                  {miniSwatch && attributeValues.length > maxItemsToShow && (
                    <MiniSwatchTooltip
                      attributeName={attributeName}
                      attributeType={attributeType}
                      attributeValues={attributeValues}
                      canonicalUrl={canonicalUrl}
                      channel={channel}
                      handleAttributeValueClick={handleAttributeValueClick}
                      handleAttributeValueHover={handleAttributeValueHover}
                      handleResetHover={handleResetHover}
                      hoveredCombinationDetails={hoveredCombinationDetails}
                      isOverflowSection={isOverflowSection}
                      isTooltipsEnabled={isTooltipsEnabled}
                      miniSwatch={miniSwatch}
                      selectedAttribute={selectedAttributeValue}
                      parentId={parentId}
                      itemId={itemId}
                    />
                  )}
                </SuperSkuCarousel>
              </SuperSkuAttribute>
            ));
        })
        : (<LoadingError onReloadClick={onReloadClick} />)}
      {miniSwatch
      && !hasSwatch(attributes)
      && canonicalUrl
      && <MoreOptions canonicalUrl={canonicalUrl} />}
      {superDuperSku && !miniSwatch && isSdskuDisplayable(superDuperSku)
      && (
        <SuperDuperSku
          channel={channel}
          handleSdskuAttributeValueClick={handleSdskuAttributeValueClick}
          superDuperSku={superDuperSku}
        />
      )}
    </div>
  );
};

SuperSku.displayName = 'SuperSku';

SuperSku.propTypes = {
  centeredCards: bool,
  disableMediaAndAvailability: bool,
  hideCards: bool,
  itemId: string.isRequired,
  large: bool,
  miniSwatch: bool,
  numberOfMiniSwatches: number,
  onChange: func,
  onHover: func,
  shouldSyncAttributes: bool,
  storeId: string,
};

SuperSku.defaultProps = {
  centeredCards: false,
  disableMediaAndAvailability: false,
  hideCards: false,
  large: false,
  miniSwatch: false,
  numberOfMiniSwatches: null,
  onChange: () => {},
  onHover: () => {},
  shouldSyncAttributes: false,
  storeId: null,
};

const Inventory = shapeType({
  enableItem: boolType(),
  totalQuantity: numberType()
});
if (Inventory.skip) {
  Inventory.skip('excludeInventory', false);
}
SuperSku.dataModel = extend({
  product: params({
    itemId: stringType().isRequired()
  }).shape({
    itemId: stringType(),
    availabilityType: shapeType({
      discontinued: boolType(),
      type: stringType()
    }),
    dataSources: stringType(), // do we need to ask for this?
    details: shapeType({
      collection: shapeType({
        collectionId: stringType()
      })
    }),
    fulfillment: client(params({ storeId: stringType() }).shape({
      backordered: boolType()
    })),
    identifiers: shapeType({
      parentId: stringType(),
      isSuperSku: boolType()
    }),
    info: shapeType({
      paintBrand: stringType()
    }),
    sizeAndFitDetail: client(shapeType({
      attributeGroups: arrayOfType({
        attributes: arrayOfType({
          attributeName: stringType(),
          dimensions: stringType()
        }),
        dimensionLabel: stringType(),
        productType: stringType()
      }),
    })),
    specificationGroup: client(arrayOfType({
      specTitle: stringType(),
      specifications: arrayOfType({
        specName: stringType(),
        specValue: stringType(),
      })
    }))
  }),
  metadata: params({
    parentId: stringType().isRequired()
  }).shape({
    attributes: arrayOfType({
      attributeName: stringType(),
      attributeValues: arrayOfType({
        swatchGuid: stringType(),
        value: stringType()
      }),
      isSwatch: boolType()
    }),
    childItemsLookup: arrayOfType({
      attributeCombination: stringType(),
      canonicalUrl: stringType(),
      isItemBackOrdered: boolType(),
      itemId: stringType()
    }),
    sizeAndFitDetail: shapeType({
      attributeGroups: arrayOfType({
        attributes: arrayOfType({
          attributeName: stringType(),
          dimensions: stringType()
        }),
        dimensionLabel: stringType(),
        productType: stringType()
      }),
    }),
    superDuperSku: shapeType({
      attributes: arrayOfType({
        attributeName: stringType(),
        attributeValues: arrayOfType({
          selected: boolType(),
          superSkuUrl: stringType(),
          value: stringType()
        })
      })
    })
  }),
  mediaPriceInventory: params({
    itemIds: arrayOfType(stringType().isRequired()).isRequired(),
    storeId: stringType().isRequired()
  }).shape({
    productDetailsList: arrayOfType({
      itemId: stringType(),
      imageLocation: stringType(),
      onlineInventory: Inventory,
      pricing: shapeType({
        value: numberType(),
        original: numberType(),
        message: stringType(), // maybe
        mapAboveOriginalPrice: boolType() // maybe
      }),
      storeInventory: Inventory
    })
  })
}, SizeGuide);
